// react
// libraries
import { useForm } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Stack } from '@mui/material';

// api
import { usePostEnergyGroupMutation } from '@/api/generated/recalc/endpoints/v2/energyGroups';
import type { GetEnergyGroupDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';
import { usePostV2LocationsMutation } from '@/api/generated/recalc/endpoints/v2/locations';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import {
  RHFAddressAutocomplete,
  RHFSelect,
  RHFTextField,
} from '@/components/ui/form';

// relative
import {
  toCreateEnergyGroupFormValues,
  toLocationDto,
  toPostEnergyGroupDto,
} from './createEnergyGroupForm.mappers';
import {
  createEnergyGroupFormSchema,
  ENERGY_GROUP_TYPE_OPTIONS,
} from './createEnergyGroupForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

export default function CreateEnergyGroupForm({
  accountId,
  onSuccess,
  onCancel,
}: {
  accountId: number;
  onSuccess: (energyGroup: GetEnergyGroupDto) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['forms', 'validation']);

  const [postLocation, { isLoading: isPostingLocation }] =
    usePostV2LocationsMutation();
  const [postEnergyGroup, { isLoading: isPostingEnergyGroup }] =
    usePostEnergyGroupMutation();
  const isCreating = isPostingLocation || isPostingEnergyGroup;

  const schema = createEnergyGroupFormSchema(t);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toCreateEnergyGroupFormValues(),
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values) => {
    const locationResult = await postLocation({
      locationDto: toLocationDto(values, accountId),
    });
    if ('error' in locationResult) return;

    const locationId = locationResult.data.locationId;
    if (locationId == null) return;

    const groupResult = await postEnergyGroup({
      postEnergyGroupDto: toPostEnergyGroupDto(values, accountId, locationId),
    });
    if ('error' in groupResult) return;

    onSuccess(groupResult.data);
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  });

  return (
    <form onSubmit={onSubmit} noValidate data-testid="energy-group-create-form">
      <Stack spacing={2}>
        <Card variant="outlined" data-testid="energy-group-create-card">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="name"
                control={control}
                required
                fullWidth
                label={t('createEnergyGroup.name.label', { ns: 'forms' })}
                placeholder={t('createEnergyGroup.name.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createEnergyGroup.name.helperText', {
                  ns: 'forms',
                })}
                disabled={isCreating}
                data-testid="energy-group-name-input"
              />

              <RHFAddressAutocomplete
                name="location"
                control={control}
                disabled={isCreating}
                required
                fullWidth
                size="small"
                label={t('createEnergyGroup.address.label', { ns: 'forms' })}
                placeholder={t('createEnergyGroup.address.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createEnergyGroup.address.helperText', {
                  ns: 'forms',
                })}
                data-testid="energy-group-address-input"
              />

              <RHFSelect
                name="type"
                control={control}
                required
                fullWidth
                label={t('createEnergyGroup.type.label', { ns: 'forms' })}
                disabled={isCreating}
                data-testid="energy-group-type-select"
                options={ENERGY_GROUP_TYPE_OPTIONS.map((type) => ({
                  value: type,
                  label: t(
                    `createEnergyGroup.type.options.${type.toLowerCase()}`,
                    { ns: 'forms' },
                  ),
                  testId: `energy-group-type-option-${type}`,
                }))}
                helperText={t('createEnergyGroup.type.helperText', {
                  ns: 'forms',
                })}
              />
            </Stack>
          </CardContent>
        </Card>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton
            onClick={onCancel}
            disabled={isCreating}
            data-testid="energy-group-create-cancel-button"
          >
            {t('common.cancel')}
          </CancelButton>
          <SubmitButton
            disabled={isCreating || !isValid}
            data-testid="energy-group-create-button"
          >
            {isCreating
              ? t('createEnergyGroup.submitting', { ns: 'forms' })
              : t('createEnergyGroup.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
